<script setup lang="ts">
import { ref } from 'vue'
import { Eye, EyeOff } from 'lucide-vue-next'
import { useLocalStorage } from '@vueuse/core'
import { Button } from '~/components/ui/button'
import {
  Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle,
} from '~/components/ui/card'
import { Checkbox } from '~/components/ui/checkbox'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { config } from '~/utils/config'
import { isSafeRedirectPath, loginFailMessage } from '~/utils/auth'

const auth = useAuthStore()
const route = useRoute()

const email = ref('')
const password = ref('')
// 記住使用者上次的「記住我」選擇；首次使用預設勾選
// （配合後端 1h vs 半年 TTL，主動勾才會拿半年 token）
const keepLogin = useLocalStorage('admin_keep_login_pref', true)
const errorMessage = ref('')
const submitting = ref(false)
const showPassword = ref(false)

async function handleSubmit() {
  errorMessage.value = ''
  submitting.value = true
  try {
    await auth.login({ email: email.value, password: password.value, keepLogin: keepLogin.value })
    // 未登入被 middleware 導來時會帶 `?redirect=`（例如審核信連結），登入成功後導回原目標
    const redirect = route.query.redirect
    await navigateTo(isSafeRedirectPath(redirect) ? redirect : '/')
  }
  catch (err) {
    const reason = (err as { data?: { reason?: string } })?.data?.reason
    errorMessage.value = loginFailMessage(reason)
  }
  finally {
    submitting.value = false
  }
}
</script>

<template>
  <form class="w-full max-w-lg" @submit.prevent="handleSubmit">
    <Card class="w-full max-w-lg">
      <CardHeader class="space-y-1 text-center">
        <CardTitle class="text-2xl font-semibold">{{ config.appName }}</CardTitle>
        <CardDescription>請登入以繼續</CardDescription>
      </CardHeader>
      <CardContent class="space-y-4">
        <div class="space-y-2">
          <Label for="email">帳號</Label>
          <Input id="email" v-model="email" type="email" placeholder="請輸入帳號（Email）" required autocomplete="username" />
        </div>
        <div class="space-y-2">
          <Label for="password">密碼</Label>
          <div class="relative">
            <Input
              id="password"
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              placeholder="請輸入密碼"
              required
              autocomplete="current-password"
              class="pr-10"
            />
            <!-- 手刻 toggle：input 內 absolute 定位的 icon button，shadcn Button 的 ghost+icon size 會帶 muted 背景與固定 32px 方塊，需大量 ! 覆寫對抗（見 ui-components.md 例外條件）。 -->
            <button
              type="button"
              class="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-r-lg"
              :aria-label="showPassword ? '隱藏密碼' : '顯示密碼'"
              :aria-pressed="showPassword"
              @click="showPassword = !showPassword"
            >
              <EyeOff v-if="showPassword" class="h-4 w-4" />
              <Eye v-else class="h-4 w-4" />
            </button>
          </div>
        </div>
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <!-- ClientOnly 包住：useLocalStorage 在 SSR 用 default、client hydration 讀 storage，
                 直接 render 會踩 hydration mismatch（server checked、client 該 unchecked）導致
                 Checkbox 內部 modelValue 鎖在 server 初值。-->
            <ClientOnly>
              <Checkbox id="keepLogin" v-model="keepLogin" />
              <template #fallback>
                <div class="size-4" />
              </template>
            </ClientOnly>
            <Label for="keepLogin" class="cursor-pointer font-normal">記住我</Label>
          </div>
          <NuxtLink to="/forgot-password" class="text-sm text-indigo-600 hover:underline">
            忘記密碼？
          </NuxtLink>
        </div>
        <p v-if="errorMessage" class="text-sm text-red-600" role="alert">{{ errorMessage }}</p>
      </CardContent>
      <CardFooter>
        <Button type="submit" class="w-full" :disabled="submitting">
          {{ submitting ? '登入中…' : '登入' }}
        </Button>
      </CardFooter>
    </Card>
  </form>
</template>
